import { canTrackGa4Events, canTrackClarityEvents } from "./webAnalyticsRuntime";
import { trackClarityEvent } from "./microsoftClarity";

const purchaseTrackedIds = new Set();

export function resetAnalyticsPurchaseDedupe() {
  purchaseTrackedIds.clear();
}

function mapCartLinesToGa4Items(lines = []) {
  return lines
    .filter((item) => item?.productID)
    .map((item) => ({
      item_id: String(item.productID),
      item_name: item.productName || "",
      price: Number(item.unitPrice) || 0,
      quantity: Number(item.qty) || 1,
    }));
}

/**
 * @param {{ productID: string, productName?: string, price?: number, currency?: string }} params
 */
export function trackAnalyticsViewItem({ productID, productName, price, currency = "INR" }) {
  try {
    if (!productID) return;

    if (canTrackGa4Events() && typeof window.gtag === "function") {
      const unitPrice = Number(price) || 0;
      window.gtag("event", "view_item", {
        currency,
        value: unitPrice,
        items: [
          {
            item_id: String(productID),
            item_name: productName || "",
            price: unitPrice,
            quantity: 1,
          },
        ],
      });
    }

    if (canTrackClarityEvents()) {
      trackClarityEvent("view_item");
    }
  } catch {
    /* ignore */
  }
}

/**
 * First add to cart only (caller/middleware enforces first-add).
 */
export function trackAnalyticsAddToCart({
  productID,
  productName,
  unitPrice,
  qty = 1,
  currency = "INR",
}) {
  try {
    if (!productID) return;

    const quantity = Number(qty) || 1;
    const price = Number(unitPrice) || 0;

    if (canTrackGa4Events() && typeof window.gtag === "function") {
      window.gtag("event", "add_to_cart", {
        currency,
        value: price * quantity,
        items: [
          {
            item_id: String(productID),
            item_name: productName || "",
            price,
            quantity,
          },
        ],
      });
    }

    if (canTrackClarityEvents()) {
      trackClarityEvent("add_to_cart");
    }
  } catch {
    /* ignore */
  }
}

/**
 * @param {{ currency?: string, value?: number, items?: Array }} params
 */
export function trackAnalyticsBeginCheckout({ currency = "INR", value, items = [] }) {
  try {
    const gaItems = mapCartLinesToGa4Items(items);
    if (!gaItems.length) return;

    const totalValue =
      value != null
        ? Number(value)
        : gaItems.reduce((sum, i) => sum + i.price * i.quantity, 0);

    if (canTrackGa4Events() && typeof window.gtag === "function") {
      window.gtag("event", "begin_checkout", {
        currency,
        value: totalValue,
        items: gaItems,
      });
    }

    if (canTrackClarityEvents()) {
      trackClarityEvent("begin_checkout");
    }
  } catch {
    /* ignore */
  }
}

/**
 * Fires once per transaction_id per browser session.
 */
export function trackAnalyticsPurchase({
  transactionId,
  value,
  currency = "INR",
  shipping,
  tax,
  coupon,
  items = [],
}) {
  try {
    if (!transactionId) return;

    const txId = String(transactionId);
    if (purchaseTrackedIds.has(txId)) return;
    purchaseTrackedIds.add(txId);

    const gaItems = mapCartLinesToGa4Items(items);
    const payload = {
      transaction_id: txId,
      currency,
      value: Number(value) || 0,
      items: gaItems,
    };

    if (shipping != null && !Number.isNaN(Number(shipping))) {
      payload.shipping = Number(shipping);
    }
    if (tax != null && !Number.isNaN(Number(tax))) {
      payload.tax = Number(tax);
    }
    if (coupon) {
      payload.coupon = String(coupon);
    }

    if (canTrackGa4Events() && typeof window.gtag === "function") {
      window.gtag("event", "purchase", payload);
    }

    if (canTrackClarityEvents()) {
      trackClarityEvent("purchase");
    }
  } catch {
    /* ignore */
  }
}
